/**
 * 쪽수 척도 변환.
 *
 * 전자책은 글자 크기·기기에 따라 쪽수가 달라진다. 그때마다 저장된 기록을
 * 고쳐 쓰면(rescaleUtils) 반올림 손실이 매번 쌓인다.
 *
 * 그래서 저장은 항상 '기준 쪽수'(book.totalPages) 하나로 통일하고,
 * 전자책 쪽수는 입력·표시 단위로만 쓴다. 배율이 바뀌면 ebookTotalPages 만
 * 갈아끼우면 되고 저장된 데이터는 건드리지 않는다.
 *
 * rescaleUtils 는 여전히 필요하다 — 기준 쪽수 자체가 틀렸거나 판을 바꿀 때 쓴다.
 */

export const PAGE_MODE = {
  PRINT: 'print',
  EBOOK: 'ebook'
};

const IDENTITY = (n) => (typeof n === 'number' ? n : null);

/**
 * 차이값(앞섬/뒤처짐 쪽수)은 toDisplay 로 바꾸면 안 된다.
 * toDisplay 는 '0쪽이 되어 사라지지 않게' 최소 1쪽을 보장하는데,
 * 차이값은 음수도 0도 그대로여야 한다.
 */
const scaleDelta = (n, ratio) => {
  if (typeof n !== 'number' || !Number.isFinite(n)) return 0;
  return Math.round(n / ratio);
};

/**
 * @param {Object} book
 * @returns {{
 *   useEbook: boolean,      전자책 쪽수로 입력·표시 중인가
 *   total: number,          화면에 보여줄 총 쪽수
 *   canonicalTotal: number, 저장 기준 총 쪽수
 *   toCanonical: (n:number)=>number|null,  입력값 → 저장값
 *   toDisplay: (n:number)=>number|null     저장값 → 화면값
 * }}
 */
export const getPageScale = (book) => {
  const canonicalTotal = book?.totalPages || 0;
  const ebookTotal = book?.ebookTotalPages || 0;
  const useEbook =
    book?.pageMode === PAGE_MODE.EBOOK && ebookTotal > 0 && canonicalTotal > 0;

  if (!useEbook) {
    return {
      useEbook: false,
      total: canonicalTotal,
      canonicalTotal,
      toCanonical: IDENTITY,
      toDisplay: IDENTITY,
      toDisplayDelta: (n) => (typeof n === 'number' && Number.isFinite(n) ? n : 0)
    };
  }

  const toCanonicalRatio = canonicalTotal / ebookTotal;

  return {
    useEbook: true,
    total: ebookTotal,
    canonicalTotal,
    toCanonical: (n) => {
      if (typeof n !== 'number' || !Number.isFinite(n)) return null;
      if (n === 0) return 0;
      // 1쪽을 넣었는데 0쪽이 되어 사라지는 일은 막는다
      return Math.max(1, Math.round(n * toCanonicalRatio));
    },
    toDisplay: (n) => {
      if (typeof n !== 'number' || !Number.isFinite(n)) return null;
      if (n === 0) return 0;
      return Math.max(1, Math.round(n / toCanonicalRatio));
    },
    toDisplayDelta: (n) => scaleDelta(n, toCanonicalRatio)
  };
};

/**
 * 일자별 독서량을 사용자가 보던 단위로 바꾼 사본.
 *
 * readingHistory 에는 기준 쪽수가 들어 있다. 통계(차트·히트맵)가 그 값을 그대로
 * 더하면, 전자책으로 60쪽 읽은 날이 40쪽으로 표시된다 — 입력한 숫자와 어긋난다.
 * 집계 전에 이 함수로 한 번 바꿔두면 그 아래 계산은 손댈 게 없다.
 *
 * 전자책 모드가 아니면 원본을 그대로 돌려준다 (사본을 만들지 않는다).
 */
export const toDisplayHistory = (book) => {
  const history = book?.readingHistory || {};
  const scale = getPageScale(book);
  if (!scale.useEbook) return history;

  const converted = {};
  Object.entries(history).forEach(([date, pages]) => {
    converted[date] = scale.toDisplay(pages) ?? 0;
  });
  return converted;
};

/** 통계용: 책 목록의 일자별 독서량을 모두 화면 단위로 바꿔 돌려준다 */
export const toDisplayBooks = (books = []) =>
  books.map(book => {
    const readingHistory = toDisplayHistory(book);
    return readingHistory === book.readingHistory ? book : { ...book, readingHistory };
  });

/** 화면용 '읽은 쪽 / 전체 쪽' 문자열 (모드에 맞는 숫자로) */
export const formatPagePair = (book, canonicalRead) => {
  const scale = getPageScale(book);
  const read = scale.toDisplay(canonicalRead) ?? 0;
  return `${read} / ${scale.total}`;
};
